import { memo, useEffect } from "react";

import { logError } from "@hl/shared-features/lib/services/logger";
import { Button, Divider, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";

import {
  ContractEntityType,
  _CollectionType,
  NftContractStandard,
} from "apollo/graphql.generated";

export type ContractTypeData =
  | {
      type: "contract";
      val: ContractEntityType;
    }
  | {
      type: "collection";
      val: _CollectionType;
    };

type CreateContractModalData = {
  isOpen: boolean;
  onClose: () => void;
  initialContract: {
    typeData: ContractTypeData;
    edited: boolean;
    data: {
      name: string;
      symbol: string;
      usingOpenseaBlocklist: boolean;
      collectionLogoImage: string;
      contractStandard: NftContractStandard;
    };
  };
  handleSubmit: (values: CreateContractFormValues) => void;
};

export interface CreateContractFormValues {
  name: string;
  symbol: string;
  contractType: ContractEntityType;
  usingOpenseaBlocklist: boolean;
  collectionLogoImage: string;
  contractStandard: NftContractStandard;
}

export const transform = (
  collectionType: _CollectionType,
  contractStandard?: NftContractStandard
): ContractEntityType => {
  if (contractStandard === NftContractStandard.ERC1155) {
    return ContractEntityType.EditionsDFS1155;
  }
  if (
    collectionType === _CollectionType.LimitedEdition ||
    collectionType === _CollectionType.OneOfOne
  ) {
    return ContractEntityType.MultipleEditionsDFS;
  } else if (collectionType === _CollectionType.OpenEdition) {
    return ContractEntityType.SingleEditionDFS;
  } else if (collectionType === _CollectionType.Series) {
    return ContractEntityType.Series;
  } else if (collectionType === _CollectionType.GenerativeSeries) {
    return ContractEntityType.GenerativeSeries;
  } else {
    logError({ collectionType }, "Unknown collection type");
    throw Error("Unknown collection type");
  }
};

const CreateContractModal = ({
  isOpen,
  initialContract,
  onClose,
  handleSubmit,
}: CreateContractModalData) => {
  const contractType =
    initialContract.typeData.type === "contract"
      ? initialContract.typeData.val
      : transform(
          initialContract.typeData.val,
          initialContract.data.contractStandard
        );

  const form = useForm<CreateContractFormValues>({
    initialValues: {
      ...initialContract.data,
      contractType,
    },
    validate: (values) => {
      return {
        name: !values.name
          ? "Name is required"
          : values.name.length < 3
          ? "Name should have more than 3 characters"
          : null,
        symbol: !values.symbol ? "Symbol is required" : null,
        contractType: !values.contractType ? "Contract type is required" : null,
      };
    },
  });

  // Resets the form to the initial data that can change after submit.
  useEffect(() => {
    form.setValues({
      ...initialContract.data,
      contractType,
    });
  }, [initialContract, contractType]);

  return (
    <Modal
      centered
      size={"lg"}
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={500} size="lg">
          {initialContract.edited ? "Edit contract" : "Customize contract"}
        </Text>
      }
    >
      <form>
        <Stack spacing={24}>
          <Text size="sm">
            Contracts are used to deploy collections on the blockchain. This
            contract will be owned and controlled solely by your wallet address.
            It will not be deployed until you finish creating this collection.
          </Text>
          <TextInput
            label="Contract name"
            description="You own and control all contracts created on Highlight. The name of this contract will be publicly visible on blockchain explorers like Etherscan."
            placeholder="Contract name"
            {...form.getInputProps("name")}
          />
          <TextInput
            label="Contract symbol"
            placeholder="CNTRCT"
            {...form.getInputProps("symbol")}
          />
          <Divider />
          <Button
            size="xl"
            onClick={() => {
              if (form.isValid()) {
                handleSubmit(form.values);
                onClose();
              } else {
                form.validate();
              }
            }}
          >
            {initialContract.edited ? "Save changes" : "Save"}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};

export default memo(CreateContractModal);
